import { useEffect, useState } from "react";

const STORAGE_KEY = "rates_discount_started_at";
const DISCOUNT_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const DISCOUNT_PERCENT = 50;

/**
 * Инициализирует или читает метку начала акции из localStorage.
 * При первом вызове записывает текущее время — оно фиксируется навсегда,
 * чтобы таймер не сбрасывался при перезагрузке страницы.
 */
const getOrInitStartedAt = (): number => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = Number(stored);
    if (!isNaN(parsed)) return parsed;
  }
  const now = Date.now();
  localStorage.setItem(STORAGE_KEY, String(now));
  return now;
};

export interface DiscountInfo {
  isActive: boolean;
  isExpired: boolean;
  timeLeft: number;
  discountPercent: number;
}

/**
 * Хук для управления акционной скидкой на базовый тариф.
 *
 * Логика работы:
 * - При первом заходе пользователя фиксирует время старта в localStorage.
 * - Скидка всегда активна (isActive = true) — показывается и после истечения 7 дней.
 * - Каждую секунду обновляет оставшееся время (timeLeft).
 * - Когда timeLeft достигает 0 — isExpired становится true и таймер скрывается,
 *   но сама скидка (цена со скидкой и бейдж) остаётся видимой.
 */
export const useRegistrationDiscount = (): DiscountInfo => {
  // Инициализируем timeLeft синхронно из localStorage, чтобы избежать
  // мигания UI при первом рендере
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    const startedAt = getOrInitStartedAt();
    const expiresAt = startedAt + DISCOUNT_DURATION_MS;
    return Math.max(0, expiresAt - Date.now());
  });

  useEffect(() => {
    const startedAt = getOrInitStartedAt();
    const expiresAt = startedAt + DISCOUNT_DURATION_MS;

    // Если акция уже истекла на момент монтирования — не запускаем интервал
    if (Date.now() >= expiresAt) return;

    const interval = setInterval(() => {
      const remaining = Math.max(0, expiresAt - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const isExpired = timeLeft <= 0;

  return {
    isActive: true,
    isExpired,
    timeLeft,
    discountPercent: DISCOUNT_PERCENT,
  };
};
